import { http } from "msw";

import { SUBSCRIPTION_ACTION, SUBSCRIPTION_STATUS } from "@/domains/subscriptions/models/constants";
import type { SubscriptionAction, SubscriptionStatus } from "@/domains/subscriptions/models/types";
import {
  createSubscription,
  MOCK_SUBSCRIPTION_LINK,
  type MockSubscriptionLine,
} from "@/mocks/data/subscriptions";
import { isoNow } from "@/mocks/dates";
import {
  careplanNotFoundResponse,
  type CareplanParams,
  findCareplan,
} from "@/mocks/handlers/careplan";
import { withSession } from "@/mocks/resolvers";
import { errorResponse, successResponse } from "@/mocks/responses";
import { mockCarts } from "@/mocks/store";
import type { CareplansWire, SubscriptionWire } from "@/mocks/types";

const STATUS_BY_ACTION: Record<SubscriptionAction, SubscriptionStatus> = {
  [SUBSCRIPTION_ACTION.RESUME]: SUBSCRIPTION_STATUS.ACTIVE,
  [SUBSCRIPTION_ACTION.REACTIVATE]: SUBSCRIPTION_STATUS.ACTIVE,
  [SUBSCRIPTION_ACTION.PAUSE]: SUBSCRIPTION_STATUS.PAUSED,
  [SUBSCRIPTION_ACTION.CANCEL]: SUBSCRIPTION_STATUS.CANCELLED,
};

const isSubscriptionAction = (action: string): action is SubscriptionAction => {
  return Object.values<string>(SUBSCRIPTION_ACTION).includes(action);
};

const subscriptionNotFoundResponse = () => {
  return errorResponse({
    status: 404,
    code: "subscription_not_found",
    message: "No subscription was found for this care plan.",
  });
};

const getCareplanProductLines = (careplan: CareplansWire): MockSubscriptionLine[] => {
  return careplan.current.products.flatMap(({ variantId }) => {
    return variantId ? [{ variantId, quantity: 1 }] : [];
  });
};

const getCartLines = (cartId: string): MockSubscriptionLine[] => {
  return (mockCarts.get(cartId)?.lines ?? []).map(({ quantity, variantId }) => {
    return { variantId, quantity };
  });
};

const getSubscriptionLines = (subscription: SubscriptionWire): MockSubscriptionLine[] => {
  return subscription.products.map(({ quantity, variantId }) => {
    return { variantId, quantity };
  });
};

export const createSubscriptionHandlers = (webappUrl: string) => {
  return [
    http.put(
      `${webappUrl}/careplans/:careplanId/subscription`,
      withSession<CareplanParams, { cartId: string }>(async ({ params, request, state }) => {
        const careplan = findCareplan(state, params.careplanId);

        if (!careplan) {
          return careplanNotFoundResponse();
        }

        const { cartId } = await request.json();
        const cartLines = getCartLines(cartId);
        const subscription = createSubscription({
          status: SUBSCRIPTION_STATUS.ACTIVE,
          acceptedAt: isoNow(),
          paymentMethod: careplan.current.subscriptions[0]?.paymentMethod,
          lines: cartLines.length > 0 ? cartLines : getCareplanProductLines(careplan),
        });

        careplan.current.subscriptions = [subscription];

        return successResponse(subscription);
      }),
    ),

    http.post(
      `${webappUrl}/careplans/:careplanId/subscription/status`,
      withSession<CareplanParams, { action: string }>(async ({ params, request, state }) => {
        const careplan = findCareplan(state, params.careplanId);

        if (!careplan) {
          return careplanNotFoundResponse();
        }

        const { action } = await request.json();

        if (!isSubscriptionAction(action)) {
          return errorResponse({
            status: 422,
            code: "validation_error",
            message: "The given data was invalid.",
            fields: { action: [`"${action}" is not a supported subscription action.`] },
          });
        }

        const currentSubscription = careplan.current.subscriptions[0];
        const status = STATUS_BY_ACTION[action];

        if (!currentSubscription && status !== SUBSCRIPTION_STATUS.ACTIVE) {
          return subscriptionNotFoundResponse();
        }

        const subscription = createSubscription({
          status,
          acceptedAt: currentSubscription?.acceptedAt ?? isoNow(),
          paymentMethod: currentSubscription?.paymentMethod,
          lines:
            status === SUBSCRIPTION_STATUS.ACTIVE || !currentSubscription
              ? getCareplanProductLines(careplan)
              : getSubscriptionLines(currentSubscription),
        });

        careplan.current.subscriptions = [subscription];

        return successResponse(subscription);
      }),
    ),

    http.get(
      `${webappUrl}/subscriptions/link`,
      withSession(({ personaId, state }) => {
        if (!state.careplan?.current.subscriptions.length) {
          return subscriptionNotFoundResponse();
        }

        return successResponse({ link: `${MOCK_SUBSCRIPTION_LINK}?customer=${personaId}` });
      }),
    ),
  ];
};
